import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, Loader2Icon, MailIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SiteFooter } from '../components/SiteFooter';
import { ChoiceChips } from '../components/ChoiceChips';
import { ACTIVATION_FORM_ENDPOINT, ACTIVATION_SUCCESS_URL, ActivationAnswers, activationMailto, emptyAnswers, featureOptions, heardFromOptions, prescriptionOptions, recommendOptions } from '../data/activation';
import { RX_INSERT_UPDATES_URL, RX_INSERT_FALLBACK_MAILTO } from '../data/rxInsert';
import { trackCustom } from '../utils/tracking';
type Status = 'idle' | 'submitting' | 'success' | 'error' | 'not_connected';
const ownerActions = [{
  title: 'Need a backup X1?',
  body: 'Keep a spare charged in the truck.',
  to: '/#offer'
}, {
  title: 'Equip your crew',
  body: 'Business / crew pack, one order.',
  to: '/#company'
}, {
  title: 'Join GritVue Crew Rewards',
  body: 'Share GritVue. Get paid.',
  to: '/rewards'
}];
const inputClass = 'mt-2 w-full border-2 border-white/20 bg-ink-950 px-4 py-3 text-[16px] text-offwhite placeholder:text-steel-500 focus:border-grit-500 focus:outline-none';
const labelClass = 'text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400';
export function Activate() {
  const [answers, setAnswers] = useState<ActivationAnswers>(emptyAnswers);
  const [status, setStatus] = useState<Status>('idle');
  const started = useRef(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    trackCustom('activation_page_visited');
  }, []);
  const update = (key: keyof ActivationAnswers, value: string) => {
    if (!started.current) {
      started.current = true;
      trackCustom('activation_started');
    }
    setAnswers(prev => ({
      ...prev,
      [key]: value
    }));
    if (status === 'error' || status === 'not_connected') setStatus('idle');
  };
  const canSubmit = answers.orderNumber.trim() !== '' && /\S+@\S+\.\S+/.test(answers.email);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || status === 'submitting') return;
    // No backend yet: say so. Nothing is stored and nothing is faked.
    if (!ACTIVATION_FORM_ENDPOINT) {
      setStatus('not_connected');
      trackCustom('activation_not_connected');
      return;
    }
    setStatus('submitting');
    try {
      const response = await fetch(ACTIVATION_FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...answers,
          source: 'gritvue_activate',
          submittedAt: new Date().toISOString()
        })
      });
      if (!response.ok) throw new Error(String(response.status));
      trackCustom('activation_completed', {
        feature: answers.feature,
        heard_from: answers.heardFrom,
        recommend: answers.recommend,
        wears_prescription: answers.wearsPrescription
      });
      if (ACTIVATION_SUCCESS_URL) {
        window.location.href = ACTIVATION_SUCCESS_URL;
        return;
      }
      setStatus('success');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch {
      setStatus('error');
    }
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1496" data-mp="s5tpp4gfeqkq">
      <PageHeader data-id="element-1497" data-mp="n7a1syi9v4pq" />
      <main className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:grid lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-8 lg:py-20" data-id="element-1498" data-mp="n7s5uhb6en87">
        <div className="lg:sticky lg:top-24 lg:self-start" data-id="element-1499" data-mp="j4deiuhsekfg">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-1500" data-mp="e6u2804nghq5">Owner activation</p>
          <h1 className="grit-headline mt-3 text-[clamp(2.5rem,8vw,4.75rem)] font-extrabold uppercase leading-[0.92]" data-id="element-1501" data-mp="9926sw4sqdh7">
            Activate your
            <br data-id="element-1502" data-mp="23mbb89ihj8sl" />
            <span className="text-grit-500" data-id="element-1503" data-mp="23mtehq3kena0">GritVue X1™</span>
          </h1>
          <p className="mt-5 max-w-lg font-display text-xl font-bold uppercase leading-snug text-offwhite" data-id="element-1504" data-mp="1yngqv34vdc6d">
            Make it official. Register your X1. Unlock owner resources. Help us build better gear.
          </p>
          <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-steel-300" data-id="element-1505" data-mp="2bniy9yjz1d5d">
            Takes about a minute. Your answers tell us who uses the X1 and what to build next.
          </p>
          <Link to="/verify" className="mt-6 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-1506" data-mp="21ojxf25g6lsx">
            Verify your production batch
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1507" data-mp="1sqhqk5z18yxq" />
          </Link>
        </div>

        <div className="mt-10 lg:mt-0" data-id="element-1508" data-mp="1dxe7tv9pgf5f">
          {status === 'success' ? <motion.div role="status" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-1509" data-mp="1iz3kffckt0vm">
              <div className="border-2 border-grit-500 bg-grit-500/10 p-6 sm:p-8" data-id="element-1510" data-mp="1928nv1zv5ajz">
                <p className="flex items-center gap-2 font-display text-3xl font-extrabold uppercase text-grit-500" data-id="element-1511" data-mp="qcdm03lmdqce">
                  <BadgeCheckIcon className="h-8 w-8" aria-hidden="true" data-id="element-1512" data-mp="lfdhhx4f4vne" />
                  Your GritVue X1 is registered.
                </p>
                <p className="mt-2 text-[15px] text-steel-200" data-id="element-1513" data-mp="udfkujbmk0du">Welcome to GritVue. Thanks for helping us build better gear.</p>
              </div>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-1514" data-mp="198v9u9xcl63d">
                {ownerActions.map(item => <li key={item.title} data-id="element-1515" data-mp="14a24x5w4mnet">
                    <Link to={item.to} className="flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1516" data-mp="1i28bq1de3i1l">
                      <span data-id="element-1517" data-mp="1n2eas2lzo6tp">
                        <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1518" data-mp="1n35vgie1sk57">{item.title}</span>
                        <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1519" data-mp="kqt22mx9o6lk">{item.body}</span>
                      </span>
                      <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1520" data-mp="tpmxwb2rwsy2" />
                    </Link>
                  </li>)}
                <li data-id="element-1521" data-mp="yho3xf9clnfn">
                  <a href={RX_INSERT_UPDATES_URL || RX_INSERT_FALLBACK_MAILTO} onClick={() => trackCustom('rx_insert_updates_clicked', {
                from: 'activation'
              })} className="flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1522" data-mp="yi7pr724cvyy">
                    <span data-id="element-1523" data-mp="1hcuy4elchbt8">
                      <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1524" data-mp="3e80ud33j7u3n">Get RX Insert updates</span>
                      <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1525" data-mp="3j6ubr6lxw593">Prototype in development.</span>
                    </span>
                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1526" data-mp="36wmjc6128ud3" />
                  </a>
                </li>
              </ul>
            </motion.div> : <form onSubmit={submit} noValidate className="grid gap-6 border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-1527" data-mp="2wygilrqzpu9t">
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-1528" data-mp="3umgrwbecjwrj">
                <div data-id="element-1529" data-mp="3ujul4dza5lad">
                  <label htmlFor="act-order" className={labelClass} data-id="element-1530" data-mp="3pn3xn5l5bnex">Order number</label>
                  <input id="act-order" required value={answers.orderNumber} onChange={e => update('orderNumber', e.target.value)} placeholder="#1001" autoComplete="off" className={inputClass} data-id="element-1531" data-mp="3knrasku9ygcc" />
                </div>
                <div data-id="element-1532" data-mp="2993zo05v27ub">
                  <label htmlFor="act-email" className={labelClass} data-id="element-1533" data-mp="299nh7frlmu7t">Email</label>
                  <input id="act-email" type="email" required value={answers.email} onChange={e => update('email', e.target.value)} placeholder="you@email.com" autoComplete="email" className={inputClass} data-id="element-1534" data-mp="256heuj7lxn6m" />
                </div>
              </div>

              <div className="grid gap-5 border-t border-white/10 pt-6" data-id="element-1535" data-mp="d6r6y47na5es">
                <div data-id="element-1536" data-mp="3vcdxvgqdtpyi">
                  <label htmlFor="act-use" className={labelClass} data-id="element-1537" data-mp="3vcx2xwuxojv4">What do you use your X1 for?</label>
                  <input id="act-use" value={answers.useCase} onChange={e => update('useCase', e.target.value)} placeholder="Engine bays, attic runs, night calls..." className={inputClass} data-id="element-1538" data-mp="451cepiguqcs" />
                </div>
                <div data-id="element-1539" data-mp="vxs3ymp7fqvp">
                  <label htmlFor="act-trade" className={labelClass} data-id="element-1540" data-mp="i4slqcy1fikg">What&apos;s your profession / trade?</label>
                  <input id="act-trade" value={answers.trade} onChange={e => update('trade', e.target.value)} placeholder="Electrician, mechanic, HVAC..." className={inputClass} data-id="element-1541" data-mp="n2u6r3f0xryk" />
                </div>
                <div data-id="element-1542" data-mp="1fk6i82dw950m">
                  <label htmlFor="act-problem" className={labelClass} data-id="element-1543" data-mp="1fkpzrhzmtre4">What problem did you most want X1 to solve?</label>
                  <input id="act-problem" value={answers.problem} onChange={e => update('problem', e.target.value)} placeholder="Holding a flashlight in my teeth..." className={inputClass} data-id="element-1544" data-mp="1ownhn2d9eulr" />
                </div>
              </div>

              <ChoiceChips legend="Which feature mattered most?" options={featureOptions} value={answers.feature} onChange={v => update('feature', v)} data-id="element-1545" data-mp="1tpqf3ll5qo78" />
              <ChoiceChips legend="Where did you first hear about GritVue?" options={heardFromOptions} value={answers.heardFrom} onChange={v => update('heardFrom', v)} data-id="element-1546" data-mp="1605u6x5g1gvb" />
              <ChoiceChips legend="Would you recommend X1 to a coworker?" options={recommendOptions} value={answers.recommend} onChange={v => update('recommend', v)} data-id="element-1547" data-mp="1fz3d9kk3oi6x" />
              <ChoiceChips legend="Do you wear prescription lenses?" options={prescriptionOptions} value={answers.wearsPrescription} onChange={v => update('wearsPrescription', v)} optional data-id="element-1548" data-mp="2rcyvj26ax72d" />

              <div data-id="element-1549" data-mp="2bc20g65sd6z8">
                <button type="submit" disabled={!canSubmit || status === 'submitting'} className="flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1550" data-mp="2bcl6aoi5w4ww">
                  {status === 'submitting' && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1551" data-mp="1nl63kumh3ll4" />}
                  {status === 'submitting' ? 'Activating...' : 'Activate my X1'}
                </button>
                {!canSubmit && <p className="mt-2 text-[12px] text-steel-500" data-id="element-1552" data-mp="1dqxnj6empl3u">Order number and a valid email are required.</p>}

                {status === 'error' && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.03] p-4 text-[14px] text-steel-200" data-id="element-1553" data-mp="383dp3twhcb2d">
                    Something went wrong and your activation wasn&apos;t saved. Try again, or email support@gritvuegear.com.
                  </p>}
                {status === 'not_connected' && <div role="status" className="mt-4 border border-white/20 bg-white/[0.03] p-4" data-id="element-1554" data-mp="3uazugk42pslx">
                    <p className="font-display text-lg font-bold uppercase text-offwhite" data-id="element-1555" data-mp="2xkqpaj8dhooo">Online activation is being connected</p>
                    <p className="mt-1 text-[14px] text-steel-300" data-id="element-1556" data-mp="31lkdldpq95i3">
                      Your answers have not been saved yet. Send them by email and we&apos;ll register your X1 by hand.
                    </p>
                    <a href={activationMailto(answers)} className="mt-3 inline-flex items-center gap-2 border-2 border-grit-500 px-4 py-2.5 font-display text-base font-extrabold uppercase text-grit-500 transition-colors duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950" data-id="element-1557" data-mp="3blb6jayn3cg2">
                      <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-1558" data-mp="3scggggps53jb" />
                      Email my activation
                    </a>
                  </div>}
                <p className="mt-4 text-[12px] leading-relaxed text-steel-500" data-id="element-1559" data-mp="b28m83qkgpl9">
                  We use your answers to support your X1 and improve our products and advertising. We never sell your data.{' '}
                  <Link to="/legal/privacy" className="underline underline-offset-2 hover:text-steel-300" data-id="element-1560" data-mp="3eip5d3wehblh">Privacy policy</Link>
                </p>
              </div>
            </form>}
        </div>
      </main>
      <SiteFooter data-id="element-1561" data-mp="3jkehynz9txbo" />
    </div>;
}